import { Skeleton } from "@bednbody/ui/components/skeleton";
import { CheckCircleIcon, MedalIcon, SparkleIcon } from "@phosphor-icons/react";
import { useMemo } from "react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { OpsWindowBooking } from "../command-center.hooks";
import type { DailyBookingStat } from "../ops-dashboard.hooks";

const TOOLTIP_STYLE = {
  background: "var(--card)",
  border: "1px solid var(--border)",
  borderRadius: "0.75rem",
  fontSize: "0.75rem",
} as const;

/**
 * Compact analytics strip: today's bookings by hour, 7-day booked value,
 * plus derived headline stats. "Booked value" is Σ gross_amount of
 * non-cancelled bookings — payment truth lives with the PayMongo webhook,
 * so this is deliberately not labeled "revenue".
 */
export function MiniAnalytics({
  window: rows,
  weekly,
  isLoading,
}: {
  window: OpsWindowBooking[];
  weekly: DailyBookingStat[];
  isLoading: boolean;
}) {
  const { byHour, completion, topHub, topService } = useMemo(() => {
    const today = new Date().toDateString();
    const hourBins = Array.from({ length: 24 }, (_, h) => ({
      hour: h,
      label: `${h}:00`,
      count: 0,
    }));
    const hubCounts = new Map<string, number>();
    const serviceCounts = new Map<string, number>();
    let done = 0;
    let past = 0;

    for (const b of rows) {
      const at = new Date(b.scheduled_at);
      const bin = hourBins[at.getHours()];
      if (at.toDateString() === today && bin) bin.count += 1;
      if (b.hubs?.name) hubCounts.set(b.hubs.name, (hubCounts.get(b.hubs.name) ?? 0) + 1);
      const svc = b.service_variants?.services?.name;
      if (svc) serviceCounts.set(svc, (serviceCounts.get(svc) ?? 0) + 1);
      if (at.getTime() < Date.now() && b.status !== "cancelled") {
        past += 1;
        if (b.status === "completed") done += 1;
      }
    }

    const top = (m: Map<string, number>) => [...m.entries()].sort((a, b) => b[1] - a[1])[0] ?? null;

    return {
      byHour: hourBins.filter((b) => b.hour >= 6), // ops day; nothing books at 3am
      completion: past > 0 ? Math.round((done / past) * 100) : null,
      topHub: top(hubCounts),
      topService: top(serviceCounts),
    };
  }, [rows]);

  if (isLoading) {
    return <Skeleton className="h-48 motion-reduce:animate-none" />;
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="flex items-center gap-2 rounded-xl bg-background px-3 py-2">
          <CheckCircleIcon size={15} className="shrink-0 text-success" />
          <div className="min-w-0">
            <p className="text-sm font-semibold tabular-nums text-foreground">
              {completion != null ? `${completion}%` : "—"}
            </p>
            <p className="truncate text-xs text-muted-foreground">7-day completion</p>
          </div>
        </div>
        <div className="flex items-center gap-2 rounded-xl bg-background px-3 py-2">
          <MedalIcon size={15} className="shrink-0 text-primary" />
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-foreground">{topHub?.[0] ?? "—"}</p>
            <p className="truncate text-xs text-muted-foreground">
              Top hub{topHub ? ` · ${topHub[1]} bookings` : ""}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 rounded-xl bg-background px-3 py-2">
          <SparkleIcon size={15} className="shrink-0 text-warning" />
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-foreground">
              {topService?.[0] ?? "—"}
            </p>
            <p className="truncate text-xs text-muted-foreground">
              Most requested{topService ? ` · ${topService[1]}×` : ""}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div>
          <p className="mb-2 text-xs font-medium text-muted-foreground">Bookings by hour (today)</p>
          <div className="h-36">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={byHour} margin={{ top: 4, right: 4, bottom: 0, left: -24 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 10, fill: "var(--muted-foreground)" }}
                  tickLine={false}
                  axisLine={false}
                  interval={2}
                />
                <YAxis
                  tick={{ fontSize: 10, fill: "var(--muted-foreground)" }}
                  tickLine={false}
                  axisLine={false}
                  allowDecimals={false}
                />
                <Tooltip
                  contentStyle={TOOLTIP_STYLE}
                  cursor={{ fill: "var(--border)", opacity: 0.4 }}
                />
                <Bar dataKey="count" name="Bookings" fill="var(--primary)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div>
          <p className="mb-2 text-xs font-medium text-muted-foreground">
            Booked value (last 7 days)
          </p>
          <div className="h-36">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={weekly} margin={{ top: 4, right: 4, bottom: 0, left: -12 }}>
                <defs>
                  <linearGradient id="mini-value-fill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="var(--primary)" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="var(--primary)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                <XAxis
                  dataKey="day"
                  tick={{ fontSize: 10, fill: "var(--muted-foreground)" }}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  tick={{ fontSize: 10, fill: "var(--muted-foreground)" }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v: number) => `₱${v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}`}
                />
                <Tooltip
                  contentStyle={TOOLTIP_STYLE}
                  formatter={(v) => [`₱${Number(v).toLocaleString("en-PH")}`, "Booked value"]}
                />
                <Area
                  type="monotone"
                  dataKey="bookedValue"
                  stroke="var(--primary)"
                  strokeWidth={2}
                  fill="url(#mini-value-fill)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}
